import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

class FirebaseService {
  // Authentication Methods

  /**
   * Create a new user with email and password
   */
  async createUser(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update profile with display name
      await updateProfile(user, { displayName });

      // Create user document in Firestore
      await this.createUserProfile(user.uid, {
        email,
        displayName,
        createdAt: serverTimestamp(),
        phoneVerified: false,
        hasPurchased: false,
        accessType: null
      });

      return { success: true, user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign in user with email and password
   */
  async signInUser(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign out current user
   */
  async signOutUser() {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Listen to authentication state changes
   */
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email) {
    try {
      if (!auth) {
        return { success: false, error: 'Firebase authentication is not configured' };
      }

      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      // Provide more specific error messages
      let userFriendlyError = 'Failed to send password reset email';

      switch (error.code) {
        case 'auth/user-not-found':
          userFriendlyError = 'No account found with this email address';
          break;
        case 'auth/invalid-email':
          userFriendlyError = 'Please enter a valid email address';
          break;
        case 'auth/too-many-requests':
          userFriendlyError = 'Too many requests. Please try again later';
          break;
        case 'auth/network-request-failed':
          userFriendlyError = 'Network error. Please check your connection';
          break;
        default:
          userFriendlyError = error.message || 'Failed to send password reset email';
      }

      return { success: false, error: userFriendlyError };
    }
  }

  // User Profile Management

  /**
   * Create user profile in Firestore with enhanced data
   */
  async createUserProfile(userId, userData) {
    try {
      // Prepare user profile data with defaults
      const profileData = {
        displayName: userData.displayName || userData.name || 'User',
        email: userData.email || null,
        phone: userData.phone || null,
        phoneVerified: userData.phoneVerified || false,
        hasPurchased: userData.hasPurchased || false,
        accessType: userData.accessType || null,
        hasUpgraded: userData.hasUpgraded || false,
        termsAcceptedAt: userData.termsAcceptedAt || null,
        loginMethod: userData.loginMethod || 'whatsapp',
        isActive: true,
        preferences: {
          theme: 'light',
          fontSize: 'medium',
          notifications: true
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };

      // Create user document only if it doesn't exist yet, so an existing
      // record (e.g. one with hasPurchased: true) is never overwritten
      const userRef = doc(db, 'users', userId);
      const created = await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(userRef);
        if (existing.exists()) return false;
        transaction.set(userRef, profileData);
        return true;
      });

      if (!created) {
        return { success: true, alreadyExists: true };
      }

      // Create user profile document for additional information
      await setDoc(doc(db, 'userProfiles', userId), {
        userId: userId,
        avatar: null,
        bio: null,
        location: null,
        interests: [],
        socialLinks: {
          website: null,
          twitter: null,
          linkedin: null
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, profile: profileData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user exists by email
   */
  async checkUserExistsByEmail(email) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return { success: true, exists: true, userId: querySnapshot.docs[0].id };
      } else {
        return { success: true, exists: false };
      }
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user profile from Firestore
   */
  async getUserProfile(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return { success: true, data: userDoc.data() };
      } else {
        return { success: false, notFound: true, error: 'User profile not found' };
      }
    } catch (error) {
      // Distinct from notFound: the profile may exist but couldn't be loaded
      return { success: false, notFound: false, error: error.message };
    }
  }

  /**
   * Update user profile in Firestore
   */
  async updateUserProfile(userId, updates) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user extended profile in Firestore
   */
  async updateUserExtendedProfile(userId, updates) {
    try {
      await updateDoc(doc(db, 'userProfiles', userId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user extended profile from Firestore
   */
  async getUserExtendedProfile(userId) {
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', userId));
      if (profileDoc.exists()) {
        return { success: true, data: profileDoc.data() };
      } else {
        return { success: false, error: 'User extended profile not found' };
      }
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(userId, preferences) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        preferences: preferences,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Deactivate user account
   */
  async deactivateUser(userId) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: false,
        deactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Reactivate user account
   */
  async reactivateUser(userId) {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: true,
        reactivatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Find user by phone number
   */
  async findUserByPhone(phoneNumber) {
    try {
      const q = query(
        collection(db, 'users'),
        where('phone', '==', phoneNumber),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
      } else {
        return { success: false, error: 'User not found' };
      }
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Find user by email address
   */
  async findUserByEmail(email) {
    try {
      const q = query(
        collection(db, 'users'),
        where('email', '==', email),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
      } else {
        return { success: false, error: 'User not found' };
      }
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all users (admin only)
   */
  async getAllUsers(limit = 50) {
    try {
      const q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(limit)
      );
      const querySnapshot = await getDocs(q);

      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: users };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Search users by name or phone
   */
  async searchUsers(searchTerm, limit = 20) {
    try {
      // Search by display name (partial match)
      const nameQuery = query(
        collection(db, 'users'),
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + '\uf8ff'),
        limit(limit)
      );

      // Search by phone number (exact match)
      const phoneQuery = query(
        collection(db, 'users'),
        where('phone', '==', searchTerm),
        limit(limit)
      );

      const [nameSnapshot, phoneSnapshot] = await Promise.all([
        getDocs(nameQuery),
        getDocs(phoneQuery)
      ]);

      const users = new Map();

      // Add users from name search
      nameSnapshot.docs.forEach(doc => {
        users.set(doc.id, { id: doc.id, ...doc.data() });
      });

      // Add users from phone search
      phoneSnapshot.docs.forEach(doc => {
        users.set(doc.id, { id: doc.id, ...doc.data() });
      });

      return { success: true, data: Array.from(users.values()) };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  // Payment and Access Management

  /**
   * Create payment record
   */
  async createPaymentRecord(paymentData) {
    try {
      const docRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, paymentId: docRef.id };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Update payment status
   */
  async updatePaymentStatus(paymentId, status, additionalData = {}) {
    try {
      await updateDoc(doc(db, 'payments', paymentId), {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user payment history
   */
  async getUserPayments(userId) {
    try {
      const q = query(
        collection(db, 'payments'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const payments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: payments };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Create book access record
   */
  async createBookAccessRecord(accessData) {
    try {
      const docRef = await addDoc(collection(db, 'bookAccess'), {
        ...accessData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true, accessId: docRef.id };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Update book access permissions
   */
  async updateBookAccess(userId, accessType, hasAccess, paymentAmount = null) {
    try {
      const updateData = {
        hasPurchased: hasAccess,
        accessType: hasAccess ? accessType : null,
        accessGrantedAt: hasAccess ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      };

      // Add payment amount if provided
      if (hasAccess && paymentAmount) {
        updateData.paymentAmount = paymentAmount;
      }

      // Add upgrade tracking for hard copy upgrades
      if (hasAccess && accessType === 'both') {
        updateData.hasUpgraded = true;
        updateData.upgradedAt = serverTimestamp();
      }

      // Use setDoc with merge: true so it creates the document if missing
      await setDoc(doc(db, 'users', userId), updateData, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Error updating book access in Firestore:', error);
      return { success: false, error: error.message };
    }
  }

  // Reading Progress Tracking

  /**
   * Save reading progress
   */
  async saveReadingProgress(userId, progressData) {
    try {
      const docRef = await addDoc(collection(db, 'readingProgress'), {
        userId,
        ...progressData,
        timestamp: serverTimestamp()
      });
      return { success: true, progressId: docRef.id };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user reading progress
   */
  async getUserReadingProgress(userId) {
    try {
      const q = query(
        collection(db, 'readingProgress'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(10)
      );
      const querySnapshot = await getDocs(q);

      const progress = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: progress };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  // Utility Methods

  /**
   * Check if a user has any completed order in the orders collection
   */
  async checkUserHasPaidOrder(userId, userEmail = null) {
    try {
      const ordersRef = collection(db, 'orders');
      const completedOrders = new Map();

      const collect = async (field, value) => {
        const snapshot = await getDocs(query(ordersRef, where(field, '==', value)));
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (data.status === 'completed') {
            completedOrders.set(docSnap.id, { id: docSnap.id, ...data });
          }
        });
      };

      // Check by userId, plus email fallback (in case userId was different or created before auth link)
      if (userId) await collect('userId', userId);
      if (userEmail) await collect('userEmail', userEmail);

      if (completedOrders.size === 0) {
        return { hasPaidOrder: false };
      }

      // Combine all orders: web + hard copy (or an explicit 'both') => 'both', else prefer hard_copy
      const orders = Array.from(completedOrders.values());
      const typeOf = (order) => order.orderType || order.product || 'web_access';
      const types = new Set(orders.map(typeOf));
      let accessType = 'web_access';
      if (types.has('both') || (types.has('hard_copy') && types.has('web_access'))) {
        accessType = 'both';
      } else if (types.has('hard_copy')) {
        accessType = 'hard_copy';
      }

      return {
        hasPaidOrder: true,
        order: orders.find(order => typeOf(order) === accessType) || orders[0],
        accessType
      };
    } catch (error) {
      console.warn('Error checking orders collection for access reconciliation:', error);
      return { hasPaidOrder: false, error: error.message };
    }
  }

  /**
   * Check if user has book access (with automatic fallback to orders collection)
   */
  async checkUserAccess(userId, userEmail = null) {
    try {
      const userProfile = await this.getUserProfile(userId);
      if (userProfile.success && userProfile.data.hasPurchased) {
        return {
          success: true,
          hasAccess: true,
          accessType: userProfile.data.accessType || null
        };
      }

      // Auto-reconciliation fallback: Check orders collection
      const orderCheck = await this.checkUserHasPaidOrder(userId, userEmail || userProfile.data?.email);
      if (orderCheck.hasPaidOrder) {
        const accessType = orderCheck.accessType || 'web_access';
        // Auto-heal the user record in Firestore so subsequent checks are instant
        await this.updateBookAccess(userId, accessType, true, orderCheck.order?.amount || null);
        return {
          success: true,
          hasAccess: true,
          accessType: accessType
        };
      }

      return { success: true, hasAccess: false };
    } catch (error) {
      console.error('Error checking user access:', error);
      return { success: false, error: error.message, hasAccess: false };
    }
  }

  /**
   * Generate unique ID
   */
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Order Management

  /**
   * Save order details to Firestore
   */
  async saveOrder(orderData) {
    try {
      const docRef = await addDoc(collection(db, 'orders'), {
        ...orderData,
        createdAt: serverTimestamp(),
        status: 'completed'
      });
      return { success: true, orderId: docRef.id };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Save hard copy order details to Firestore
   */
  async saveHardCopyOrder(orderData) {
    try {
      const docRef = await addDoc(collection(db, 'orders'), {
        ...orderData,
        productType: 'hard_copy_upgrade',
        createdAt: serverTimestamp(),
        status: 'completed'
      });
      return { success: true, orderId: docRef.id };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user orders
   */
  async getUserOrders(userId) {
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: orders };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all orders (for admin)
   */
  async getAllOrders() {
    try {
      const q = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return { success: true, data: orders };
    } catch (error) {
      // Production: Error handled silently
      return { success: false, error: error.message };
    }
  }
}

const firebaseServiceInstance = new FirebaseService();
export default firebaseServiceInstance;
